import type { ReactNode } from "react";
import {
  Drawer as DrawerBase,
  IconButton,
  type DrawerProps,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { useIsVirtualKeyboardOpen } from "../lib/hooks/useIsVirtualKeyboardOpen";

type DrawerTopBarProps = {
  onCloseClick: () => void;
  title: ReactNode;
};

export function DrawerTopBar({ onCloseClick, title }: DrawerTopBarProps) {
  return (
    <div className="mb-4 flex min-h-11 items-center">
      <IconButton onClick={onCloseClick} aria-label="close drawer">
        <XIcon size={20} className="fill-text-interactive" weight="bold" />
      </IconButton>

      <h2 className="grow text-center font-medium">{title}</h2>

      <div className="size-9 shrink-0" />
    </div>
  );
}

type DrawerToBarWithActionProps = DrawerTopBarProps & {
  actionElement?: ReactNode;
};

export function DrawerTopBarWithAction({
  onCloseClick,
  title,
  actionElement,
}: DrawerToBarWithActionProps) {
  return (
    <div className="grid min-h-11 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
      <IconButton
        onClick={onCloseClick}
        className="justify-self-start"
        aria-label="close drawer"
      >
        <XIcon size={20} className="fill-text-interactive" weight="bold" />
      </IconButton>
      <div className="justify-self-center overflow-hidden text-ellipsis whitespace-nowrap">
        {title}
      </div>
      <div className="justify-self-end">{actionElement}</div>
    </div>
  );
}

type Props = Pick<DrawerProps, "open" | "disableRestoreFocus" | "children"> & {
  onClose?: () => void;
  hasFullHeight?: boolean;
  heightOffset?: number;
  backgroundColor?: string;
};

export function Drawer({
  open,
  children,
  onClose,
  hasFullHeight,
  disableRestoreFocus,
  backgroundColor,
  heightOffset = 0,
}: Props) {
  const heightOffsetWithUnit = `${heightOffset}px`;

  const heightStyle = hasFullHeight
    ? `calc(100dvh - ${heightOffsetWithUnit})`
    : "auto";

  const { isVirtualKeyboardOpen, virtualKeyboardHeight } =
    useIsVirtualKeyboardOpen();

  return (
    <DrawerBase
      anchor="bottom"
      open={open}
      onClose={(e, reason) => {
        // do not allow to close the drawer on backdrop click
        if (reason === "backdropClick") return;
        onClose?.();
      }}
      disableRestoreFocus={disableRestoreFocus}
      sx={{
        "& > .MuiDrawer-paper": {
          height: heightStyle,
          backgroundColor,
          ...(isVirtualKeyboardOpen
            ? { paddingBottom: `${virtualKeyboardHeight}px` }
            : {}),
        },
      }}
    >
      {children}
    </DrawerBase>
  );
}
